/**
 * @file BrandMarkGlyph.tsx
 * @brief The Verblizr mark (the curve and the play) as a one-colour glyph.
 *
 * @details
 * Mirrors the mobile app's `FRONTEND/src/shared/components/brand/BrandMarkGlyph.tsx`
 * (DES 08-10-2026): the same two filled paths, DES's artwork
 * (`APPS BRANDING/SVG/justsimplewhite_logo.svg`), untouched, filled with
 * `color`. `BrandMarkSlideIcon` is the mark as a `SwipeButton` knob icon
 * (Slide to Interpret / Slide for Boardroom on Home, Slide to Start
 * Interpretation), as on the phone.
 *
 * @author DES
 * @date 08-10-2026
 */

import { forwardRef } from "react";
import type { LucideProps } from "lucide-react";

const MARK =
  "M57.34,38.67c-1.32,4.04-3.56,7.85-6.72,11.13-3.3,3.42-7.22,5.88-11.42,7.37-.23.08-.39-.21-.21-.37.64-.54,1.26-1.13,1.85-1.74,2.57-2.69,4.42-5.8,5.52-9.11.23-.69-.37-1.38-1.09-1.26l-5.88,1.02c-2.23.38-4.02-1.73-3.22-3.73l5.9-16.13c.52-1.4.21-2.98-.8-4.09-.35-.38-.72-.76-1.1-1.12-9.68-9.32-25.1-9.010-34.4.67-2.45,2.55-4.23,5.48-5.35,8.61-.08.23-.42.17-.42-.07-.11-7.49,2.61-15.02,8.21-20.84,11.27-11.71,29.9-12.07,41.61-.81.57.55,1.12,1.11,1.63,1.69,1.03,1.16,1.33,2.8.79,4.26l-7.25,19.84c-.97,2.41,1.2,4.98,3.89,4.51l7.25-1.24c.8-.14,1.48.63,1.22,1.4Z";
const PLAY =
  "M13.11,35.35l-.06-8.04c0-.93.98-1.52,1.74-1.06l7.02,3.97c.82.46.83,1.68.02,2.16l-6.97,4.07c-.75.41-1.75-.16-1.76-1.09Z";

export function BrandMarkGlyph({
  size,
  color,
  className,
}: {
  size: number;
  color: string;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 57.4 57.19"
      className={className}
      aria-hidden
    >
      <path d={MARK} fill={color} />
      <path d={PLAY} fill={color} />
    </svg>
  );
}

/**
 * The mark as a `SwipeButton` knob icon: a drop-in for a lucide icon there
 * (takes `className` / `color` / `size`; the stroke props mean nothing to a
 * filled mark). Drawn a step smaller than the box, as the phone draws it at
 * 26 against the line glyphs' 30: filled, it reads heavier. Takes the
 * knob's text colour (`currentColor`).
 */
export const BrandMarkSlideIcon = forwardRef<SVGSVGElement, Omit<LucideProps, "ref">>(
  function BrandMarkSlideIcon({ className, color = "currentColor", size }, ref) {
    return (
      <svg
        ref={ref}
        width={size}
        height={size}
        // 57.4 / (26 / 30) ≈ 66.2, centred on the mark.
        viewBox="-4.4 -4.5 66.2 66.2"
        className={className}
        aria-hidden
      >
        <path d={MARK} fill={color} />
        <path d={PLAY} fill={color} />
      </svg>
    );
  },
);

export default BrandMarkGlyph;
